import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import ContractorDashboard from './pages/contractor-dashboard';
import JobDetailsAndApplications from './pages/job-details-and-applications';
import ClientJobManagement from './pages/client-job-management';
import Login from './pages/login';
import AffiliateDashboard from './pages/affiliate-dashboard';
import SuperAdminDashboard from './pages/super-admin-dashboard';
import UserProfileManagement from './pages/user-profile-management';
import ContractorJobBrowser from './pages/contractor-job-browser';
import JobSectorBrowserElectricalServices from './pages/job-sector-browser-electrical-services';
import JobSectorBrowserHVACsystems from './pages/job-sector-browser-hvac-systems';
import JobSectorBrowserPlumbingServices from './pages/job-sector-browser-plumbing-services';
import JobSectorBrowserSolarPanelServices from './pages/job-sector-browser-solar-panel-services';
import JobSectorBrowserPlumbingSpecialists from './pages/job-sector-browser-plumbing-specialists-loodgieters';
import JobSectorBrowserCraneOperators from './pages/job-sector-browser-crane-operators-kraan-operators';
import JobSectorBrowserRoadConstructionWorkers from './pages/job-sector-browser-road-construction-workers-stratenmakers-grondwerkers';
import Homepage from './pages/homepage';
import OpdrachtPlaatsen from './pages/opdracht-plaatsen';
import OpdrachtZoeken from './pages/opdracht-zoeken';
import WordtPartner from './pages/wordt-partner';
import ZonnepanelenPage from './pages/zonnepanelen';
import WarmtepompPage from './pages/warmtepomp';
import ThuisbatterijPage from './pages/thuisbatterij';
import OnderhoudPage from './pages/onderhoud';
import VentilatiePage from './pages/ventilatie';
import ElektrischPage from './pages/elektrisch';
import ProfileCompletionWizardStep1 from './pages/profile-completion-wizard-step-1-role-confirmation';
import ProfileCompletionWizardStep2 from './pages/profile-completion-wizard-step-2-role-specific-information';
import ProfileCompletionWizardStep3 from './pages/profile-completion-wizard-step-3-verification-and-dashboard-access';
import { useAuth } from './contexts/AuthContext';

const ProtectedRoute = ({ children, allowedRoles = null }) => {
  const { user, userProfile, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Laden...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check role-based access if allowedRoles is specified
  if (allowedRoles && userProfile?.role && !allowedRoles?.includes(userProfile?.role)) {
    // Redirect to appropriate dashboard based on user role
    if (userProfile?.role === 'contractor') {
      return <Navigate to="/contractor-job-browser" replace />;
    } else if (userProfile?.role === 'client') {
      return <Navigate to="/client-job-management" replace />;
    } else if (userProfile?.role === 'affiliate') {
      return <Navigate to="/affiliate-dashboard" replace />;
    } else if (userProfile?.role === 'superadmin') {
      return <Navigate to="/super-admin-dashboard" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return children;
};

// New component to check profile completion
const ProfileCompletionCheck = ({ children }) => {
  const { user, userProfile, loading } = useAuth();
  const location = window.location?.pathname;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Laden...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Skip profile completion check for superadmin
  if (userProfile?.role === 'superadmin') {
    return children;
  }

  // Skip if already on profile completion pages
  if (location?.includes('profile-completion-wizard')) {
    return children;
  }

  // Redirect to profile completion if not completed
  if (userProfile && !userProfile?.profile_completed) {
    return <Navigate to="/profile-completion-wizard-step-1-role-confirmation" replace />;
  }

  return children;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ScrollToTop />
      <RouterRoutes>
        <Route path="/" element={<Homepage />} />
        <Route path="/homepage" element={<Homepage />} />
        <Route path="/login" element={<Login />} />
        
        {/* Profile Completion Wizard - No profile completion check */}
        <Route 
          path="/profile-completion-wizard-step-1-role-confirmation" 
          element={
            <ProtectedRoute>
              <ProfileCompletionWizardStep1 />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/profile-completion-wizard-step-2-role-specific-information" 
          element={
            <ProtectedRoute>
              <ProfileCompletionWizardStep2 />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/profile-completion-wizard-step-3-verification-and-dashboard-access" 
          element={
            <ProtectedRoute>
              <ProfileCompletionWizardStep3 />
            </ProtectedRoute>
          } 
        />
        
        {/* Public service pages - require login to see detailed content */}
        <Route 
          path="/opdrachten/zonnepanelen" 
          element={
            <ProfileCompletionCheck>
              <ProtectedRoute allowedRoles={['client', 'contractor', 'affiliate', 'superadmin']}>
                <ZonnepanelenPage />
              </ProtectedRoute>
            </ProfileCompletionCheck>
          } 
        />
        <Route 
          path="/opdrachten/warmtepomp" 
          element={
            <ProfileCompletionCheck>
              <ProtectedRoute allowedRoles={['client', 'contractor', 'affiliate', 'superadmin']}>
                <WarmtepompPage />
              </ProtectedRoute>
            </ProfileCompletionCheck>
          } 
        />
        <Route 
          path="/opdrachten/thuisbatterij" 
          element={
            <ProfileCompletionCheck>
              <ProtectedRoute allowedRoles={['client', 'contractor', 'affiliate', 'superadmin']}>
                <ThuisbatterijPage />
              </ProtectedRoute>
            </ProfileCompletionCheck>
          } 
        />
        <Route 
          path="/opdrachten/onderhoud" 
          element={
            <ProfileCompletionCheck>
              <ProtectedRoute allowedRoles={['client', 'contractor', 'affiliate', 'superadmin']}>
                <OnderhoudPage />
              </ProtectedRoute>
            </ProfileCompletionCheck>
          } 
        />
        <Route 
          path="/opdrachten/ventilatie" 
          element={
            <ProfileCompletionCheck>
              <ProtectedRoute allowedRoles={['client', 'contractor', 'affiliate', 'superadmin']}>
                <VentilatiePage />
              </ProtectedRoute>
            </ProfileCompletionCheck>
          } 
        />
        <Route 
          path="/opdrachten/elektrisch" 
          element={
            <ProfileCompletionCheck>
              <ProtectedRoute allowedRoles={['client', 'contractor', 'affiliate', 'superadmin']}>
                <ElektrischPage />
              </ProtectedRoute>
            </ProfileCompletionCheck>
          } 
        />
        
        {/* Client-only routes - opdrachtgevers zien werkzoekenden */}
        <Route 
          path="/opdracht-plaatsen" 
          element={
            <ProfileCompletionCheck>
              <ProtectedRoute allowedRoles={['client']}>
                <OpdrachtPlaatsen />
              </ProtectedRoute>
            </ProfileCompletionCheck>
          } 
        />
        <Route 
          path="/client-job-management" 
          element={
            <ProfileCompletionCheck>
              <ProtectedRoute allowedRoles={['client']}>
                <ClientJobManagement />
              </ProtectedRoute>
            </ProfileCompletionCheck>
          } 
        />
        <Route 
          path="/job-details-and-applications" 
          element={
            <ProfileCompletionCheck>
              <ProtectedRoute allowedRoles={['client']}>
                <JobDetailsAndApplications />
              </ProtectedRoute>
            </ProfileCompletionCheck>
          } 
        />
        
        {/* Contractor-only routes - werkzoekenden zien opdrachten en opdrachtgevers */}
        <Route 
          path="/opdracht-zoeken" 
          element={
            <ProfileCompletionCheck>
              <ProtectedRoute allowedRoles={['contractor']}>
                <OpdrachtZoeken />
              </ProtectedRoute>
            </ProfileCompletionCheck>
          } 
        />
        <Route 
          path="/contractor-job-browser" 
          element={
            <ProfileCompletionCheck>
              <ProtectedRoute allowedRoles={['contractor', 'affiliate']}>
                <ContractorJobBrowser />
              </ProtectedRoute>
            </ProfileCompletionCheck>
          } 
        />
        <Route 
          path="/job-sector-browser-electrical-services" 
          element={
            <ProfileCompletionCheck>
              <ProtectedRoute allowedRoles={['contractor', 'affiliate', 'client']}>
                <JobSectorBrowserElectricalServices />
              </ProtectedRoute>
            </ProfileCompletionCheck>
          } 
        />
        <Route 
          path="/job-sector-browser-hvac-systems" 
          element={
            <ProfileCompletionCheck>
              <ProtectedRoute allowedRoles={['contractor', 'affiliate', 'client']}>
                <JobSectorBrowserHVACsystems />
              </ProtectedRoute>
            </ProfileCompletionCheck>
          } 
        />
        <Route 
          path="/job-sector-browser-plumbing-services" 
          element={
            <ProfileCompletionCheck>
              <ProtectedRoute allowedRoles={['contractor', 'affiliate', 'client']}>
                <JobSectorBrowserPlumbingServices />
              </ProtectedRoute>
            </ProfileCompletionCheck>
          } 
        />
        <Route 
          path="/job-sector-browser-solar-panel-services" 
          element={
            <ProfileCompletionCheck>
              <ProtectedRoute allowedRoles={['contractor', 'affiliate', 'client']}>
                <JobSectorBrowserSolarPanelServices />
              </ProtectedRoute>
            </ProfileCompletionCheck>
          } 
        />
        <Route 
          path="/job-sector-browser-plumbing-specialists-loodgieters" 
          element={
            <ProfileCompletionCheck>
              <ProtectedRoute allowedRoles={['contractor', 'affiliate', 'client']}>
                <JobSectorBrowserPlumbingSpecialists />
              </ProtectedRoute>
            </ProfileCompletionCheck>
          } 
        />
        <Route 
          path="/job-sector-browser-crane-operators-kraan-operators" 
          element={
            <ProfileCompletionCheck>
              <ProtectedRoute allowedRoles={['contractor', 'affiliate', 'client']}>
                <JobSectorBrowserCraneOperators />
              </ProtectedRoute>
            </ProfileCompletionCheck>
          } 
        />
        <Route 
          path="/job-sector-browser-road-construction-workers-stratenmakers-grondwerkers" 
          element={
            <ProfileCompletionCheck>
              <ProtectedRoute allowedRoles={['contractor', 'affiliate', 'client']}>
                <JobSectorBrowserRoadConstructionWorkers />
              </ProtectedRoute>
            </ProfileCompletionCheck>
          } 
        />
        <Route 
          path="/contractor-dashboard" 
          element={
            <ProfileCompletionCheck>
              <ProtectedRoute allowedRoles={['contractor']}>
                <ContractorDashboard />
              </ProtectedRoute>
            </ProfileCompletionCheck>
          } 
        />
        
        {/* Affiliate/Partner routes - partners zien zowel opdrachten als werkzoekenden */}
        <Route 
          path="/wordt-partner" 
          element={
            <ProfileCompletionCheck>
              <ProtectedRoute allowedRoles={['affiliate']}>
                <WordtPartner />
              </ProtectedRoute>
            </ProfileCompletionCheck>
          } 
        />
        <Route 
          path="/affiliate-dashboard" 
          element={
            <ProfileCompletionCheck>
              <ProtectedRoute allowedRoles={['affiliate']}>
                <AffiliateDashboard />
              </ProtectedRoute>
            </ProfileCompletionCheck>
          } 
        />
        
        {/* Super Admin routes */}
        <Route 
          path="/super-admin-dashboard" 
          element={
            <ProtectedRoute allowedRoles={['superadmin']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } 
        />
        
        {/* Profile management - all authenticated users */}
        <Route 
          path="/user-profile-management" 
          element={
            <ProfileCompletionCheck>
              <ProtectedRoute>
                <UserProfileManagement />
              </ProtectedRoute>
            </ProfileCompletionCheck>
          } 
        />
        
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
